import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcPaymentsV1IncomingWireQueryServiceIncomingWireGet } from '@interxfi/api-client/generated/hooks/ixc-payments-v1-incoming-wire-query-service-incoming-wire-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcPaymentsV1IncomingWireState } from '@interxfi/types/generated/api';
import { PaymentsIncomingWireEntityMenu } from '../../../../../../../../../../features/payments/incoming-wire/payments-incoming-wire-entity-menu';
import { PaymentsIncomingWireEventSummary } from '../../../../../../../../../../features/payments/incoming-wire/payments-incoming-wire-event';
import { getPaymentsIncomingWireStateListItems } from '../../../../../../../../../../features/payments/incoming-wire/payments-incoming-wire-list-items';
import { getPaymentsIncomingWireDetailPath } from '../../../../../../../../../../features/payments/incoming-wire/payments-incoming-wire-routing';
import { PaymentsIncomingWireStatusPill } from '../../../../../../../../../../features/payments/incoming-wire/payments-incoming-wire-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/finance/_finance/payment/incoming-wire/_incoming-wire/$wireId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `incoming-wire-payment-${params.wireId}`,
        label: 'Incoming Wire',
        to: getPaymentsIncomingWireDetailPath(params.wireId),
        suffix: <CopyButton content={params.wireId} label="Copy Wire ID" tooltip={{ copy: params.wireId, copied: 'Copied Wire ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Incoming Wire Payment'),
  component: PaymentsIncomingWireDetail,
});

function getListItems(data: IxcPaymentsV1IncomingWireState | undefined) {
  const items = getPaymentsIncomingWireStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [items.data.spec.amount],
        },
      ] satisfies ReadonlyListGroup[],
      right: [
        items.metadata.createdAt,
        items.metadata.updatedAt,
        items.wireId,
        items.data.spec.accountNumberId,
        items.data.spec.transferId,
        items.data.spec.accountId,
        items.data.spec.programId,
      ] satisfies ReadonlyListItem[],
    },
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load incoming wire', 'view this incoming wire');

function PaymentsIncomingWireDetail() {
  const { wireId } = Route.useParams();
  const request = { wireId } as const;
  const { data, error, isPending } = useIxcPaymentsV1IncomingWireQueryServiceIncomingWireGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.incomingWire), [data?.incomingWire]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <PaymentsIncomingWireStatusPill isPending={showPending} size="large" status={data?.incomingWire?.status} />
          </HorizontalStack>

          <PaymentsIncomingWireEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <PaymentsIncomingWireEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
